/**
 * engine/recent.ts — «недавние»: every track that really started, sent to the server in the
 * order it started.
 *
 * Task 11. Checklist: «В недавние трек попадает, когда воспроизведение реально началось, а не
 * при неудачном нажатии». The player decides what «really started» means — the element's first
 * `playing` for a track (`engine/player.ts`, `played`) — and this module only carries it to
 * `PUT /api/recent/:id`. The list itself is read by «Моё» from the server, like any list.
 *
 * ## In order, one at a time
 *
 * The server puts whichever request it gets last on top. Two tracks started a second apart and
 * sent at once could arrive swapped, and the older one would sit on top. So one request is out
 * at a time, and the next waits for its answer.
 *
 * ## A failure is tried again
 *
 * A request that did not get through stays at the head of the line and goes out again after
 * {@link RECENT_RETRY_MS}, or with the next track that starts if that is sooner — before it, so
 * the order holds. A `404` is dropped instead: the track left the channel, and asking again
 * would get the same answer. Nothing is kept across a reload: a track that started while the
 * server could not be reached, in an app then closed before it could be, is not in «Недавние».
 */

import { markPlayed } from '../lib/api';
import type { ApiResult } from '../lib/api';
import { useSession } from '../store/session';

/**
 * The longest line kept while the server cannot be reached. The server keeps a hundred
 * (`apps/api/src/routes/my.ts`, `RECENT_KEPT`); anything older would be trimmed on arrival.
 */
export const RECENT_BACKLOG = 100;

/** How long a line that could not be sent waits before it is tried again on its own. */
export const RECENT_RETRY_MS = 30_000;

export type SendPlayed = (trackId: number) => Promise<ApiResult<unknown>>;

export interface RecentLog {
  /** `trackId` has really started. Does not wait. */
  played(trackId: number): void;
  /** Sends nothing more, not even what is waiting. */
  stop(): void;
}

export function createRecentLog(send: SendPlayed): RecentLog {
  /** Oldest first. A track appears once: started again, it moves to the end. */
  let waiting: number[] = [];
  let busy = false;
  let stopped = false;
  let retry: ReturnType<typeof setTimeout> | undefined;

  async function pump(): Promise<void> {
    const head = waiting[0];
    if (busy || stopped || head === undefined) return;
    if (retry !== undefined) clearTimeout(retry);
    retry = undefined;
    busy = true;
    let delivered = false;
    try {
      const result = await send(head);
      delivered = result.ok || result.error.status === 404;
    } catch {
      delivered = false;
    } finally {
      busy = false;
    }
    if (stopped) return;
    if (!delivered) {
      retry = setTimeout(() => {
        retry = undefined;
        void pump();
      }, RECENT_RETRY_MS);
      return;
    }
    // Started again behind another track while its request was out, it moved back in the line
    // and must go once more. Started again with nothing in between, it is still the head, and
    // the request that just landed already says what the second one would.
    if (waiting[0] === head) waiting = waiting.slice(1);
    await pump();
  }

  return {
    played(trackId) {
      waiting = [...waiting.filter((id) => id !== trackId), trackId].slice(-RECENT_BACKLOG);
      void pump();
    },
    stop() {
      stopped = true;
      if (retry !== undefined) clearTimeout(retry);
      retry = undefined;
    },
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * The instance the app uses
 * ──────────────────────────────────────────────────────────────────────────── */

const LIVE_SEND: SendPlayed = async (trackId) =>
  await markPlayed(useSession.getState().session?.token, trackId);

let log = createRecentLog(LIVE_SEND);

/** The production answer to the player's `played`. */
export function recordPlayed(trackId: number): void {
  log.played(trackId);
}

/**
 * A fresh, empty line — with the real request, or with `send`. For tests: the line is
 * module-level and outlives every shell a test renders.
 */
export function resetRecentLog(send: SendPlayed = LIVE_SEND): void {
  log.stop();
  log = createRecentLog(send);
}
